import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Search, Filter, X, Loader2, Clock, Grape, Baby, Tag, MapPin, Map, Mountain } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useLocation } from '@/contexts/LocationContext';
import { useAuth } from '@/contexts/AuthContext';
import { useKidsFriendly } from '@/contexts/KidsFriendlyContext';
import { api } from '@/lib/api';
import { getOpenStatus } from '@/lib/openingHours';
import { getDistanceKm } from '@/lib/distance';
import SEO from '@/components/SEO';
import CategoryCard from '@/components/CategoryCard';
import PlaceCard from '@/components/PlaceCard';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';

const ExplorePage = () => {
  const { t, getLocalizedField, language } = useLanguage();
  const { location } = useLocation();
  const { user } = useAuth();
  const { kidsFriendlyFilter } = useKidsFriendly();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [categories, setCategories] = useState([]);
  const [places, setPlaces] = useState([]);
  const [favorites, setFavorites] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [activeCategories, setActiveCategories] = useState((searchParams.get('category') || '').split(',').filter(Boolean));
  const [openNowOnly, setOpenNowOnly] = useState(searchParams.get('open') === '1');
  const [officialOnly, setOfficialOnly] = useState(searchParams.get('official') === '1');
  const [kidsOnly, setKidsOnly] = useState(searchParams.get('kids') === '1');
  const [suggestions, setSuggestions] = useState(null);
  const [showSuggest, setShowSuggest] = useState(false);
  const suggestTimer = useRef(null);

  useEffect(() => {
    loadCategories();
  }, []);

  useEffect(() => {
    loadPlaces();
    // searchQuery se aplica al enviar el buscador, no en cada tecla
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCategories, kidsFriendlyFilter, officialOnly, kidsOnly]);

  useEffect(() => {
    clearTimeout(suggestTimer.current);
    if (searchQuery.trim().length < 2) { setSuggestions(null); return; }
    suggestTimer.current = setTimeout(() => {
      api.suggestPlaces(searchQuery.trim(), language).then((r) => setSuggestions(r.data)).catch(() => setSuggestions(null));
    }, 250);
    return () => clearTimeout(suggestTimer.current);
  }, [searchQuery, language]);

  useEffect(() => {
    if (user) {
      loadFavorites();
    }
  }, [user]);

  const loadCategories = async () => {
    try {
      const res = await api.getCategories();
      setCategories(res.data);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const loadPlaces = async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (activeCategories.length) params.category = activeCategories.join(',');
      if (searchQuery) params.search = searchQuery;
      if (kidsFriendlyFilter || kidsOnly) params.kids_friendly = true;
      if (officialOnly) params.official_guachinche = true;
      
      const res = await api.getPlaces(params);
      setPlaces(res.data);
    } catch (error) {
      console.error('Error loading places:', error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate distances client-side - updates live without reloading
  const sortedPlaces = useMemo(() => {
    return places
      .filter(p => !openNowOnly || ['open', 'open24'].includes(getOpenStatus(p.opening_hours).state))
      .map(p => ({
        ...p,
        distance: location?.lat ? parseFloat(getDistanceKm(location.lat, location.lng, p.latitude, p.longitude).toFixed(2)) : null
      })).sort((a, b) => (a.distance ?? 999) - (b.distance ?? 999));
  }, [places, location, openNowOnly]);

  const openNowCount = useMemo(
    () => places.filter(p => ['open', 'open24'].includes(getOpenStatus(p.opening_hours).state)).length,
    [places]
  );

  const toggleOpenNow = () => {
    const next = !openNowOnly;
    setOpenNowOnly(next);
    setSearchParams(prev => {
      if (next) prev.set('open', '1'); else prev.delete('open');
      return prev;
    });
  };

  const loadFavorites = async () => {
    try {
      const res = await api.getFavorites();
      setFavorites(new Set(res.data.map(p => p.id)));
    } catch (error) {
      console.error('Error loading favorites:', error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchParams(prev => {
      if (searchQuery) {
        prev.set('search', searchQuery);
      } else {
        prev.delete('search');
      }
      return prev;
    });
    loadPlaces();
  };

  const setParam = (key, on) => setSearchParams(prev => { if (on) prev.set(key, '1'); else prev.delete(key); return prev; });
  const toggleOfficial = () => { const n = !officialOnly; setOfficialOnly(n); setParam('official', n); };
  const toggleKids = () => { const n = !kidsOnly; setKidsOnly(n); setParam('kids', n); };

  const handleCategoryClick = (slug, add = false) => {
    const next = activeCategories.includes(slug) && !add
      ? activeCategories.filter(c => c !== slug)
      : activeCategories.includes(slug) ? activeCategories : [...activeCategories, slug];
    setActiveCategories(next);
    setSearchParams(prev => {
      if (next.length) prev.set('category', next.join(',')); else prev.delete('category');
      return prev;
    });
  };

  const pickSuggestion = (type, value) => {
    setShowSuggest(false);
    if (type === 'place') { navigate(`/place/${value}`); return; }
    setSearchQuery('');
    setSearchParams(prev => { prev.delete('search'); return prev; });
    if (type === 'category') handleCategoryClick(value, true);
    if (type === 'seal') { setOfficialOnly(true); setParam('official', true); }
  };

  const handleFavoriteClick = async (placeId) => {
    if (!user) {
      toast.error('Inicia sesión para guardar favoritos');
      return;
    }

    try {
      if (favorites.has(placeId)) {
        await api.removeFavorite(placeId);
        setFavorites(prev => {
          const next = new Set(prev);
          next.delete(placeId);
          return next;
        });
        toast.success('Eliminado de favoritos');
      } else {
        await api.addFavorite(placeId);
        setFavorites(prev => new Set(prev).add(placeId));
        toast.success('Añadido a favoritos');
      }
    } catch (error) {
      toast.error('Error al actualizar favoritos');
    }
  };

  const clearFilters = () => {
    setSearchQuery('');
    setActiveCategories([]);
    setOfficialOnly(false);
    setKidsOnly(false);
    setOpenNowOnly(false);
    setSearchParams({});
  };

  const catName = (slug) => getLocalizedField(categories.find(c => c.slug === slug), 'name') || slug;
  const activeSubtitle = activeCategories.length === 1 ? getLocalizedField(categories.find(c => c.slug === activeCategories[0]), 'subtitle') : '';
  const L = { es: { official: 'Solo oficiales', kids: 'Para niños', clear: 'Limpiar filtros', cats: 'Categorías', seals: 'Sellos', placesT: 'Lugares', trails: 'Rutas', placesCount: 'lugares', viewMap: 'Ver en mapa', mapHint: 'Ver estos lugares en el mapa' }, en: { official: 'Official only', kids: 'For kids', clear: 'Clear filters', cats: 'Categories', seals: 'Seals', placesT: 'Places', trails: 'Routes', placesCount: 'places', viewMap: 'View on map', mapHint: 'See these places on the map' }, de: { official: 'Nur offizielle', kids: 'Für Kinder', clear: 'Filter löschen', cats: 'Kategorien', seals: 'Siegel', placesT: 'Orte', trails: 'Routen', placesCount: 'Orte', viewMap: 'Karte', mapHint: 'Diese Orte auf der Karte sehen' } }[language] || {};

  const mapUrl = useMemo(() => {
    const q = new URLSearchParams();
    if (activeCategories.length) q.set('category', activeCategories.join(','));
    if (openNowOnly) q.set('open', '1');
    if (officialOnly) q.set('official', '1');
    if (kidsOnly) q.set('kids', '1');
    const s = q.toString();
    return `/map${s ? `?${s}` : ''}`;
  }, [activeCategories, openNowOnly, officialOnly, kidsOnly]);

  return (
    <div className="pb-20" data-testid="explore-page">
      <SEO
        title={language === 'en' ? 'Explore Tenerife - Best Places' : language === 'de' ? 'Teneriffa entdecken - Beste Orte' : 'Explora Tenerife - Mejores Lugares'}
        description={language === 'en' ? 'Discover the best places in Tenerife: beaches, restaurants, natural pools, viewpoints, wineries and more. With Google ratings and distance from you.' : language === 'de' ? 'Entdecken Sie die besten Orte auf Teneriffa: Strande, Restaurants, Naturpools, Aussichtspunkte, Weinguter und mehr.' : 'Descubre los mejores lugares de Tenerife: playas, restaurantes, piscinas naturales, miradores, bodegas y mas. Con valoraciones de Google y distancia desde tu ubicacion.'}
        path="/explore"
        lang={language}
      />
      {/* Header */}
      <div className="bg-white">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between gap-3 mb-4">
            <h1 className="text-2xl font-bold text-gray-900">
              {t('explore')}
            </h1>
            <div className="flex items-center gap-2">
              <button onClick={() => navigate('/trails')} className="flex items-center gap-1.5 px-3 py-2 rounded-full bg-white border border-[#2F9E5B]/40 text-[#2F9E5B] text-sm font-semibold shadow-sm hover:bg-[#2F9E5B]/5 active:scale-95 transition-[background-color,transform]" data-testid="explore-trails-btn">
                <Mountain size={16} /> {L.trails}
              </button>
              <button
                onClick={() => navigate(mapUrl)}
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#006D77] text-white text-sm font-semibold shadow-md hover:bg-[#005A63] active:scale-95 transition-[background-color,transform]"
                title={L.mapHint}
                data-testid="explore-view-map-btn"
              >
                <Map size={16} /> {L.viewMap}
              </button>
            </div>
          </div>
          
          {/* Search */}
          <form onSubmit={handleSearch} className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <Input
                type="text"
                placeholder={`${t('search')}...`}
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setShowSuggest(true); }}
                onFocus={() => setShowSuggest(true)}
                onBlur={() => setTimeout(() => setShowSuggest(false), 150)}
                autoComplete="off"
                className="pl-10 bg-white border-gray-200 rounded-lg"
                data-testid="explore-search-input"
              />
              {showSuggest && suggestions && (suggestions.categories.length + suggestions.seals.length + suggestions.places.length > 0) && (
                <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-gray-200 shadow-xl z-30 max-h-80 overflow-y-auto" data-testid="search-suggestions">
                  {suggestions.categories.length > 0 && (
                    <div className="py-1">
                      <p className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wide text-gray-400">{L.cats}</p>
                      {suggestions.categories.map((c) => (
                        <button key={c.slug} type="button" onMouseDown={() => pickSuggestion('category', c.slug)} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-[#38B2AC]/5" data-testid={`suggest-category-${c.slug}`}>
                          <Tag size={14} className="text-[#38B2AC]" /> {c.name}
                        </button>
                      ))}
                    </div>
                  )}
                  {suggestions.seals.length > 0 && <p className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wide text-gray-400 border-t border-gray-50">{L.seals}</p>}
                  {suggestions.seals.map((sl) => (
                    <button key={sl.key} type="button" onMouseDown={() => pickSuggestion('seal', sl.key)} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-[#7B2D3A]/5" data-testid="suggest-seal-official">
                      <Grape size={14} className="text-[#7B2D3A]" /> <span className="text-[#7B2D3A] font-medium">{sl.label}</span>
                    </button>
                  ))}
                  {suggestions.places.length > 0 && (
                    <div className="py-1 border-t border-gray-50">
                      <p className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wide text-gray-400">{L.placesT}</p>
                      {suggestions.places.map((p) => (
                        <button key={p.id} type="button" onMouseDown={() => pickSuggestion('place', p.id)} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-gray-50" data-testid={`suggest-place-${p.id}`}>
                          <MapPin size={14} className="text-gray-400" /> <span className="flex-1 truncate">{p.name}</span>
                          {p.official_guachinche && <Grape size={12} className="text-[#7B2D3A]" />}
                          <span className="hidden sm:inline text-[11px] text-gray-400">{catName(p.category)}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            <Button type="submit" className="bg-[#38B2AC] hover:bg-[#319795] text-white rounded-full px-6">
              {t('search')}
            </Button>
          </form>

          {/* Active filters */}
          {(activeCategories.length > 0 || searchQuery || officialOnly || kidsOnly || openNowOnly) && (
            <div className="flex items-center gap-2 mt-4 flex-wrap" data-testid="active-filters">
              {activeCategories.map((slug) => (
                <Badge key={slug} variant="secondary" className="bg-[#38B2AC]/10 text-[#319795] hover:bg-[#38B2AC]/20" data-testid={`active-filter-${slug}`}>
                  {catName(slug)}
                  <button onClick={() => handleCategoryClick(slug)} className="ml-1">
                    <X size={14} />
                  </button>
                </Badge>
              ))}
              {openNowOnly && (
                <Badge variant="secondary" className="bg-emerald-50 text-emerald-700" data-testid="active-filter-open">
                  {language === 'es' ? 'Abiertos ahora' : language === 'de' ? 'Jetzt geöffnet' : 'Open now'}<button onClick={toggleOpenNow} className="ml-1"><X size={14} /></button>
                </Badge>
              )}
              {officialOnly && (
                <Badge variant="secondary" className="bg-[#7B2D3A]/10 text-[#7B2D3A]" data-testid="active-filter-official">
                  {L.official}<button onClick={toggleOfficial} className="ml-1"><X size={14} /></button>
                </Badge>
              )}
              {kidsOnly && (
                <Badge variant="secondary" className="bg-[#38B2AC]/10 text-[#319795]" data-testid="active-filter-kids">
                  {L.kids}<button onClick={toggleKids} className="ml-1"><X size={14} /></button>
                </Badge>
              )}
              {searchQuery && (
                <Badge variant="secondary" className="bg-[#38B2AC]/10 text-[#319795]">
                  "{searchQuery}"
                  <button onClick={() => { setSearchQuery(''); loadPlaces(); }} className="ml-1">
                    <X size={14} />
                  </button>
                </Badge>
              )}
              <button
                onClick={clearFilters}
                className="text-sm text-gray-500 hover:text-[#38B2AC]"
              >
                {L.clear}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Categories */}
      <div className="max-w-7xl mx-auto px-4 py-4">
        <h2 className="font-medium text-sm text-gray-600 mb-3">{t('categories')}</h2>
        <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
          <button
            onClick={toggleOpenNow}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-full whitespace-nowrap text-sm transition-all border ${
              openNowOnly
                ? 'bg-emerald-600 text-white border-emerald-600'
                : 'bg-white text-emerald-700 border-emerald-200 hover:border-emerald-500'
            }`}
            data-testid="open-now-filter"
          >
            <Clock size={14} />
            {language === 'es' ? 'Abiertos ahora' : language === 'de' ? 'Jetzt geöffnet' : 'Open now'}
            <span className={`text-xs ${openNowOnly ? 'text-white/80' : 'text-emerald-600/70'}`}>({openNowCount})</span>
          </button>
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => handleCategoryClick(category.slug)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full whitespace-nowrap text-sm transition-all border ${
                activeCategories.includes(category.slug)
                  ? 'bg-[#38B2AC] text-white border-[#38B2AC]'
                  : 'bg-white text-gray-700 border-gray-200 hover:border-[#38B2AC]'
              }`}
              data-testid={`category-filter-${category.slug}`}
            >
              {getLocalizedField(category, 'name')}
            </button>
          ))}
          <button onClick={toggleOfficial}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-full whitespace-nowrap text-sm transition-all border ${officialOnly ? 'bg-[#7B2D3A] text-white border-[#7B2D3A]' : 'bg-white text-[#7B2D3A] border-[#7B2D3A]/30 hover:border-[#7B2D3A]'}`}
            data-testid="official-only-filter">
            <Grape size={14} /> {L.official}
          </button>
          <button onClick={toggleKids}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-full whitespace-nowrap text-sm transition-all border ${kidsOnly ? 'bg-[#38B2AC] text-white border-[#38B2AC]' : 'bg-white text-[#319795] border-[#38B2AC]/30 hover:border-[#38B2AC]'}`}
            data-testid="kids-only-filter">
            <Baby size={14} /> {L.kids}
          </button>
        </div>
      </div>

      {/* Places grid */}
      <div className="max-w-7xl mx-auto px-4 pb-8">
        <div className="flex items-center justify-between mb-4">
          <div className="min-w-0">
            <h2 className="font-medium text-sm text-gray-600">
              {activeCategories.length ? activeCategories.map(catName).join(' · ') : t('allPlaces')}
            </h2>
            {activeSubtitle && (
              <p className="text-xs text-gray-500 mt-0.5" data-testid="explore-category-subtitle">{activeSubtitle}</p>
            )}
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-gray-400" data-testid="explore-results-count">
              {sortedPlaces.length} {L.placesCount}
            </span>
            <button onClick={() => navigate(mapUrl)} className="flex items-center gap-1 text-xs font-medium text-[#006D77] hover:underline" data-testid="explore-view-map-link">
              <Map size={13} /> {L.viewMap}
            </button>
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => (
              <Skeleton key={i} className="aspect-[4/3]" />
            ))}
          </div>
        ) : sortedPlaces.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">{t('noResults')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {sortedPlaces.map((place) => (
              <PlaceCard
                key={place.id}
                place={place}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ExplorePage;
